/**
 * Connectors: typed client over the local `api` helper (M3.3).
 *
 * Mirrors the M3.1/M3.2 REST surface (every response is the `{data,error,meta}`
 * envelope). Shared DTOs come from `@tabbio/types`; do NOT use
 * `@tabbio/api-client` (dead). Entitlement (Free-cap) errors surface as a typed
 * {@link ConnectorsApiError} so callers can branch on `code` and open the
 * upgrade modal instead of showing an error.
 */
import type {
  APIResponse,
  Connection,
  ConnectorConnectRequest,
  ConnectorListResponse,
  ConnectorScopesResult,
  ConnectorSessionResult,
  EntitlementDecision,
} from '@tabbio/types';
import { api } from './api';

// ── Error codes the connect flow branches on ─────────────────────────────────

export const CONNECTOR_ENTITLEMENT_DENIED = 'SUBSCRIPTION_ENTITLEMENT_DENIED';

/** Entitlement decision attached to a 403 SUBSCRIPTION_ENTITLEMENT_DENIED. */
export type ConnectorEntitlementDecision = EntitlementDecision;

export class ConnectorsApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly meta?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ConnectorsApiError';
  }

  get decision(): ConnectorEntitlementDecision | undefined {
    return this.meta?.decision as ConnectorEntitlementDecision | undefined;
  }
}

export function isConnectorEntitlementDenied(
  error: unknown,
): error is ConnectorsApiError {
  return (
    error instanceof ConnectorsApiError &&
    error.code === CONNECTOR_ENTITLEMENT_DENIED
  );
}

function unwrap<T>(res: APIResponse<T>): T {
  if (res.error) {
    throw new ConnectorsApiError(
      res.error.message,
      res.error.code,
      res.meta as Record<string, unknown> | undefined,
    );
  }
  if (res.data == null) {
    throw new ConnectorsApiError('Empty response', 'EMPTY_RESPONSE');
  }
  return res.data;
}

// ── Catalog + connections ─────────────────────────────────────────────────────

/** GET /api/connectors: catalog + the viewer's connections grouped by rail. */
export async function listConnectors(): Promise<ConnectorListResponse> {
  return unwrap(await api.get<ConnectorListResponse>('/api/connectors'));
}

// ── Connect ────────────────────────────────────────────────────────────────────

/** POST /api/connectors/session: short-lived Nango Connect session token (tool+oauth). */
export async function createConnectSession(
  connectorSlug: string,
  returnUrl?: string,
): Promise<ConnectorSessionResult> {
  return unwrap(
    await api.post<ConnectorSessionResult>('/api/connectors/session', {
      connectorSlug,
      ...(returnUrl ? { returnUrl } : {}),
    }),
  );
}

/**
 * POST /api/connectors/:slug/connect: persist a connection.
 * Tool+oauth: pass the `nangoConnectionId` the Connect UI returned.
 * Tool+webhook (Zapier/Make): no body needed: the server mints the secret.
 * Count-gated → throws {@link ConnectorsApiError} with code
 * SUBSCRIPTION_ENTITLEMENT_DENIED at the cap.
 */
export async function connectConnector(
  slug: string,
  input: ConnectorConnectRequest = {},
): Promise<Connection> {
  const res = unwrap(
    await api.post<{ connection: Connection }>(
      `/api/connectors/${slug}/connect`,
      input,
    ),
  );
  return res.connection;
}

// ── Revoke ──────────────────────────────────────────────────────────────────────

/** DELETE /api/connectors/connections/:id: revoke a connection. */
export async function revokeConnection(
  connectionId: string,
): Promise<Connection> {
  const res = unwrap(
    await api.delete<{ connection: Connection }>(
      `/api/connectors/connections/${connectionId}`,
    ),
  );
  return res.connection;
}

// ── Human-readable granted scopes ─────────────────────────────────────────────

/** GET /api/connectors/connections/:id/scopes: human-readable granted scopes. */
export async function getConnectionScopes(
  connectionId: string,
): Promise<ConnectorScopesResult> {
  return unwrap(
    await api.get<ConnectorScopesResult>(
      `/api/connectors/connections/${connectionId}/scopes`,
    ),
  );
}
